import { HttpService } from '../HttpService/HttpService';
import { APIS } from '../../utils/apiConfig';
import GlobalConfig from '../../config/GlobalConfig';

export interface GoogleAuthResponse {
  sc: number; // Backend uses 'sc' not 'status'
  result: {
    // Backend uses 'result' not 'data'
    token: string;
    user: {
      Id: number;
      EmailAddress: string;
      FirstName: string;
      LastName: string;
      RoleId: number;
      RoleName: string;
      CompanyName?: string;
      ProfilePicture?: string;
      AccountId?: string;
      RemainingDays?: number | null;
      SubscriptionStatus?: string | null;
      UserAccounts?: any[];
    };
    message?: string;
  };
}

export interface OAuthStatusResponse {
  sc: number; // Backend uses 'sc' not 'status'
  result: {
    // Backend uses 'result' not 'data'
    configured: boolean;
    message: string;
  };
}

export class GoogleAuthService {
  constructor(private httpService: HttpService) {}

  /**
   * Initiates Google OAuth login by redirecting to backend OAuth endpoint
   */
  initiateGoogleLogin(): void {
    const backendUrl = GlobalConfig.apiBaseUrl || '';
    window.location.href = `${backendUrl}api/auth/google`;
  }

  /**
   * Retrieves the authentication token after OAuth callback
   * This should be called from the success page after Google redirects back
   */
  async retrieveAuthToken(): Promise<GoogleAuthResponse> {
    try {
      const response = await this.httpService.post(
        APIS.GOOGLE_AUTH_TOKEN(),
        {}
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message ||
          'Failed to retrieve authentication token'
      );
    }
  }

  /**
   * Checks if Google OAuth is properly configured on the backend
   */
  async checkOAuthStatus(): Promise<OAuthStatusResponse> {
    try {
      const response = await this.httpService.get(APIS.OAUTH_STATUS());
      return response.data;
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message || 'Failed to check OAuth status'
      );
    }
  }

  /**
   * Handles successful authentication by storing token and user data
   */
  handleAuthSuccess(token: string, user: any): void {
    // Remove LOGIN_ACCESS flag (used by ProtectedRoute to force login)
    localStorage.removeItem('LOGIN_ACCESS');

    // Store token in localStorage (app uses 'auth-state' as the key)
    localStorage.setItem('auth-state', token);
    localStorage.setItem('token', token); // Also store as 'token' for compatibility

    // Store token expiry time (24 hours from now)
    const expiryTime = Date.now() + 24 * 60 * 60 * 1000;
    localStorage.setItem('token_expiry_time', expiryTime.toString());

    // Store user data
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('userEmail', user.EmailAddress);
    localStorage.setItem('userName', `${user.FirstName} ${user.LastName}`);
    localStorage.setItem('RoleId', user.RoleId?.toString() || '');
    localStorage.setItem('RoleName', user.RoleName || 'User');
    localStorage.setItem('UserId', user.Id?.toString() || '');

    // ✅ FIXED: Store RemainingDays (required for trial/subscription logic)
    if (user.RemainingDays !== undefined && user.RemainingDays !== null) {
      localStorage.setItem('RemainingDays', user.RemainingDays.toString());
    } else {
      localStorage.setItem('RemainingDays', 'null');
    }

    if (user.CompanyName) {
      localStorage.setItem('companyName', user.CompanyName);
      localStorage.setItem('CompanyName', user.CompanyName);
    }

    if (user.ProfilePicture) {
      localStorage.setItem('profilePicture', user.ProfilePicture);
    }

    if (user.AccountId) {
      localStorage.setItem('AccountId', user.AccountId.toString());
    }
  }

  /**
   * Clears authentication data (for logout)
   */
  clearAuthData(): void {
    localStorage.removeItem('auth-state');
    localStorage.removeItem('token');
    localStorage.removeItem('token_expiry_time');
    localStorage.removeItem('user');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('companyName');
    localStorage.removeItem('CompanyName');
    localStorage.removeItem('profilePicture');
    localStorage.removeItem('RoleId');
    localStorage.removeItem('RoleName');
    localStorage.removeItem('UserId');
    localStorage.removeItem('AccountId');
  }
}
